import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { groupBy } from 'lodash-es';
import { RGedeeldePaginaPersoon } from '@cumlaude/service-contract';
import { GedeeldePaginaPersoonFilter, RestService, stripQueryParams } from '@cumlaude/shared-services';
import { findDashboardLeaf } from '@cumlaude/shared-dashboard-registry';
import { BerichtRijComponent } from '../bericht-rij/bericht-rij.component';
import { GedeeldePaginaPersoonGroep, GedeeldePaginaPersoonMode } from '../gedeelde-pagina-persoon.model';

/**
 * De backend pagineert over losse GedeeldePaginaPersoon-rijen, terwijl de UI groepen per bericht
 * toont. Eén groep kan dus over meerdere blokken verspreid zijn, waardoor er net zo lang blokken
 * bijgehaald worden tot er genoeg complete groepen zijn.
 */
@Component({
	selector: 'app-berichten-lijst',
	templateUrl: './berichten-lijst.component.html',
	styleUrl: './berichten-lijst.component.scss',
	imports: [BerichtRijComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BerichtenLijstComponent implements OnInit {
	private readonly restService = inject(RestService);

	mode = input.required<GedeeldePaginaPersoonMode>();
	filter = input.required<GedeeldePaginaPersoonFilter>();
	refresh = output<void>();

	private readonly rijen = signal<RGedeeldePaginaPersoon[]>([]);

	/** Het exacte totaal uit de Content-Range. Default infinity zodat allesGeladen() false is en de lus überhaupt begint. */
	private readonly totaalRijen = signal(Number.POSITIVE_INFINITY);
	private readonly allesGeladen = computed(() => this.rijen().length >= this.totaalRijen());

	/** Ophogen bij refresh; een request die daarna nog binnenkomt herkent zo dat zijn resultaat achterhaald is. */
	private generatie = 0;

	private lopendeOphaalActie: Promise<void> | null = null;

	protected readonly zichtbaarAantal = signal(5);

	private readonly alleGroepen = computed(() => this.groupByMessageUUID(this.rijen(), this.mode()));

	protected readonly groepen = computed(() => {
		const groepen = this.alleGroepen();
		if (this.mode() === GedeeldePaginaPersoonMode.OUTBOX && !this.allesGeladen() && groepen.length > 0) {
			return groepen.slice(0, -1);
		}
		return groepen;
	});

	protected readonly zichtbareGroepen = computed(() => this.groepen().slice(0, this.zichtbaarAantal()));

	protected readonly meerBeschikbaar = computed(() => this.groepen().length > this.zichtbaarAantal() || !this.allesGeladen());

	ngOnInit() {
		this.zorgVoorGenoegGroepen();
	}

	protected async laadMeer() {
		this.zichtbaarAantal.update((aantal) => aantal + 10);
		await this.zorgVoorGenoegGroepen();
	}

	protected async opRijRefresh() {
		this.generatie++;
		this.rijen.set([]);
		this.totaalRijen.set(Number.POSITIVE_INFINITY);
		await this.zorgVoorGenoegGroepen();
		this.refresh.emit();
	}

	private zorgVoorGenoegGroepen(): Promise<void> {
		if (!this.lopendeOphaalActie) {
			this.lopendeOphaalActie = this.ophaalLus().finally(() => {
				this.lopendeOphaalActie = null;
			});
		}
		return this.lopendeOphaalActie;
	}

	private async ophaalLus() {
		while (this.groepen().length < this.zichtbaarAantal() && !this.allesGeladen()) {
			await this.laadVolgendBlok();
		}
	}

	private async laadVolgendBlok() {
		const generatieBijStart = this.generatie;
		const pagina = await firstValueFrom(this.restService.getGedeeldePaginaPersonen(this.filter(), this.rijen().length));
		if (this.generatie !== generatieBijStart) return;

		this.rijen.update((rijen) => [...rijen, ...pagina.items]);
		this.totaalRijen.set(pagina.totaal);
	}

	private groupByMessageUUID(values: RGedeeldePaginaPersoon[], type: GedeeldePaginaPersoonMode): GedeeldePaginaPersoonGroep[] {
		return Object.entries(groupBy(values, 'messageUUID')).map(([messageUUID, items]) => {
			const primary = items[0];

			return {
				type,
				naam: this.getNaam(items, type),
				namen: this.getNamen(items, type),
				messageUUID,
				dashboard: primary.dashboard,
				hoofdpagina: findDashboardLeaf(stripQueryParams(primary.state))?.hoofdpagina,
				bericht: primary.bericht,
				berichtWeergave: this.resolveBericht(primary.bericht, type),
				verzonden: primary.verzonden,
				ongelezen: type === GedeeldePaginaPersoonMode.INBOX ? !items[0].gelezen : undefined,
				primary,
			};
		});
	}

	private getNaam(items: RGedeeldePaginaPersoon[], type: GedeeldePaginaPersoonMode): string {
		if (type === GedeeldePaginaPersoonMode.INBOX) return items[0].verzender?.naam ?? 'Onbekend';

		const namen = this.getNamen(items, type);
		if (namen.length === 0) return '';
		if (namen.length === 1) return namen[0];

		return `${namen.length} ontvangers`;
	}

	private resolveBericht(bericht: string | undefined, type: GedeeldePaginaPersoonMode): string {
		if (bericht?.trim()) return bericht;

		return type === GedeeldePaginaPersoonMode.INBOX ? 'heeft een overzicht met je gedeeld.' : 'Overzicht gedeeld';
	}

	private getNamen(items: RGedeeldePaginaPersoon[], type: GedeeldePaginaPersoonMode): string[] {
		if (type === GedeeldePaginaPersoonMode.INBOX) return [items[0].verzender?.naam ?? 'Onbekend'];

		return items.map((item) => item.ontvanger.naam).sort((a, b) => a.localeCompare(b));
	}
}
